import { useState, useEffect, useRef, useMemo } from 'react'
import { useAuth } from './contexts/AuthContext'
import { Login } from './pages/Login'
import { SettingsPage } from './pages/SettingsPage'
import { Button, Input, Card, CardHeader, CardTitle, CardContent, Alert, CreateClientForm, ClientPayments, CreateMatterForm, MatterDetail, ClientDetail, Modal, AiAssistant, CreateMerchantForm, AcceptTermsForm, PlanSelector, TemplateLibrary, FeedbackWidget } from './components'
import { useTenantService, TenantResponse } from './services/tenantService'
import { useClientService, Client, ClientStatus, CLIENT_STATUSES, CreateClientResult } from './services/clientService'
import { useMatterService, Matter, MatterType, Task } from './services/matterService'
import { useInviteService } from './services/inviteService'
import { usePaymentService, Payment } from './services/paymentService'
import { matterDisplayStatus, clientStatusClasses } from './statusStyles'
import { TerminologyProvider, resolveTerminology, Terminology } from './contexts/TerminologyContext'

const matterTypes: MatterType[] = ['Sale', 'Purchase', 'Transfer', 'Subdivision']

const TAB_PATHS = ['/dashboard', '/clients', '/invitations', '/matters', '/payments', '/templates'] as const

// Labels for /clients and /matters come from the tenant's own terminology
// override (see TerminologyContext.tsx) rather than being static — everything
// else keeps its fixed English label.
function tabLabel(path: string, terminology: Terminology): string {
  if (path === '/clients') return terminology.clients
  if (path === '/matters') return terminology.matters
  if (path === '/invitations') return 'Invitations'
  if (path === '/payments') return 'Payments'
  if (path === '/templates') return 'Templates'
  return 'Dashboard'
}

const TAB_ICON_PATHS: Record<string, string> = {
  '/dashboard': 'M3.75 6a2.25 2.25 0 0 1 2.25-2.25h12A2.25 2.25 0 0 1 20.25 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75a2.25 2.25 0 0 1 2.25-2.25h5.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25h-2.25a2.25 2.25 0 0 1-2.25-2.25v-2.25Z',
  '/clients': 'M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z',
  '/invitations': 'M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75',
  '/matters': 'M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-19.5 0v6a2.25 2.25 0 0 0 2.25 2.25h15a2.25 2.25 0 0 0 2.25-2.25v-6m-19.5 0v-.243a2.25 2.25 0 0 1 .659-1.591L5.432 7.4a2.25 2.25 0 0 1 1.591-.659h9.954a2.25 2.25 0 0 1 1.591.659l2.523 2.516a2.25 2.25 0 0 1 .659 1.591v.243',
  '/payments': 'M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z',
  '/templates': 'M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z',
}

const buildDefaultInviteSubject = (matterType: MatterType | '') =>
  matterType ? `Action needed: your ${matterType.toLowerCase()} details` : 'Action needed: your details'

const buildDefaultInviteMessage = (matterType: MatterType | '', link: string, matterLabel: string) => {
  const topic = matterType ? `your ${matterType.toLowerCase()}` : `your ${matterLabel.toLowerCase()}`
  return `Hi,\n\nTo get things moving on ${topic}, could you please fill out your details using the secure link below? It only takes a couple of minutes:\n\n${link}\n\nIf you have any questions, just get in touch.\n\nThanks!`
}

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

function App() {
  const { isAuthenticated, logout, token, isLoading, email } = useAuth()
  const [tenantDetails, setTenantDetails] = useState<TenantResponse | null>(null)
  const terminology = useMemo(
    () => resolveTerminology(tenantDetails?.tenant.terminology),
    [tenantDetails?.tenant.terminology],
  )
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null)
  const [route, setRoute] = useState(() => window.location.pathname)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [clients, setClients] = useState<Client[]>([])
  const [clientsLoading, setClientsLoading] = useState(false)
  const [clientsError, setClientsError] = useState<string | null>(null)
  const [clientStatusFilter, setClientStatusFilter] = useState<ClientStatus | 'all'>('all')
  const [showAddClientModal, setShowAddClientModal] = useState(false)
  const [showAddMatterModal, setShowAddMatterModal] = useState(false)
  const [matters, setMatters] = useState<Matter[]>([])
  const [mattersLoading, setMattersLoading] = useState(false)
  const [mattersError, setMattersError] = useState<string | null>(null)
  const [matterTasksByMatterId, setMatterTasksByMatterId] = useState<Record<string, Task[]>>({})
  const [allPayments, setAllPayments] = useState<Payment[]>([])
  const [inviteLink, setInviteLink] = useState<string | null>(null)
  const [inviteLoading, setInviteLoading] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [inviteCopied, setInviteCopied] = useState(false)
  const [inviteMatterType, setInviteMatterType] = useState<MatterType | ''>('')
  const [inviteClientEmail, setInviteClientEmail] = useState('')
  const [inviteSubject, setInviteSubject] = useState('')
  const [inviteMessage, setInviteMessage] = useState('')
  const inviteInputRef = useRef<HTMLInputElement>(null)
  const { fetchTenantDetails } = useTenantService()
  const { listClients, updateClientStatus } = useClientService()
  const { listMatters, listTasks } = useMatterService()
  const { createInvite } = useInviteService()
  const { listPayments } = usePaymentService()

  useEffect(() => {
    if (isAuthenticated) {
      loadTenantDetails()
    }
  }, [isAuthenticated])

  // Clients/matters are behind the onboarding gate anyway (terms → plan →
  // merchant setup) — no point fetching them until the tenant is actually
  // set up to use them.
  useEffect(() => {
    if (tenantDetails?.isMerchantConfigured) {
      loadClients()
      loadMatters()
    }
  }, [tenantDetails?.isMerchantConfigured])

  useEffect(() => {
    const onPopState = () => setRoute(window.location.pathname)
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  // Powers the tasks-progress column on the Matters table — refetched
  // whenever the set of matter ids changes (not on every matters update,
  // since closing a matter shouldn't re-fetch every other matter's tasks).
  const matterIdsKey = matters.map((m) => m.id).sort().join(',')
  useEffect(() => {
    if (matters.length === 0) {
      setMatterTasksByMatterId({})
      return
    }

    let cancelled = false
    ;(async () => {
      const entries = await Promise.all(
        matters.map(async (m) => {
          try {
            return [m.id, await listTasks(m.id)] as const
          } catch {
            return [m.id, [] as Task[]] as const
          }
        })
      )
      if (!cancelled) setMatterTasksByMatterId(Object.fromEntries(entries))
    })()

    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterIdsKey])

  // Powers the "Total Collected" tiles and the Payments tab — one fetch of
  // every client's payments, shared across all three rather than each tab
  // re-fetching its own copy.
  const clientIdsKey = clients.map((c) => c.id).sort().join(',')
  useEffect(() => {
    if (clients.length === 0) {
      setAllPayments([])
      return
    }

    let cancelled = false
    ;(async () => {
      const results = await Promise.all(
        clients.map(async (c) => {
          try {
            return await listPayments(c.id)
          } catch {
            return []
          }
        })
      )
      if (!cancelled) {
        setAllPayments(results.flat())
      }
    })()

    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientIdsKey])

  useEffect(() => {
    if (!email) return
    setTermsAccepted(localStorage.getItem(`mc_terms_accepted_${email}`) === 'true')
    setSelectedPlan(localStorage.getItem(`mc_plan_selected_${email}`))
  }, [email])

  const handleAcceptTerms = () => {
    if (email) localStorage.setItem(`mc_terms_accepted_${email}`, 'true')
    setTermsAccepted(true)
  }

  const handleSelectPlan = (planId: string) => {
    if (email) localStorage.setItem(`mc_plan_selected_${email}`, planId)
    setSelectedPlan(planId)
  }

  const navigate = (path: string) => {
    window.history.pushState({}, '', path)
    setRoute(path)
    setSidebarOpen(false)
  }

  const loadTenantDetails = async () => {
    if (!token) return

    try {
      const details = await fetchTenantDetails()
      if (details) {
        setTenantDetails(details)
      }
    } catch (err) {
      console.error('Failed to fetch tenant details:', err)
    }
  }

  const loadClients = async () => {
    setClientsLoading(true)
    setClientsError(null)
    try {
      const results = await listClients()
      setClients(results)
    } catch (err) {
      setClientsError(err instanceof Error ? err.message : 'Failed to load clients')
    } finally {
      setClientsLoading(false)
    }
  }

  const loadMatters = async () => {
    setMattersLoading(true)
    setMattersError(null)
    try {
      const results = await listMatters()
      setMatters(results)
    } catch (err) {
      setMattersError(err instanceof Error ? err.message : 'Failed to load matters')
    } finally {
      setMattersLoading(false)
    }
  }

  const handleClientStatusChange = async (clientId: string, status: ClientStatus) => {
    setClientsError(null)
    try {
      const updated = await updateClientStatus(clientId, status)
      setClients((prev) => prev.map((c) => (c.id === clientId ? updated : c)))
    } catch (err) {
      setClientsError(err instanceof Error ? err.message : 'Failed to update client status')
    }
  }

  const clientLabel = (clientId: string) => {
    const c = clients.find((client) => client.id === clientId)
    return c ? (c.fullName || `${c.firstName} ${c.lastName}`) : clientId
  }

  const handleGenerateInvite = async () => {
    setInviteError(null)
    setInviteCopied(false)
    setInviteLoading(true)
    try {
      const invite = await createInvite(inviteMatterType ? { matterType: inviteMatterType } : {})
      const link = `${window.location.origin}/client-intake/${invite.id}`
      setInviteLink(link)
      setInviteSubject(buildDefaultInviteSubject(inviteMatterType))
      setInviteMessage(buildDefaultInviteMessage(inviteMatterType, link, terminology.matter))
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to generate invite link')
    } finally {
      setInviteLoading(false)
    }
  }

  const handleCopyInvite = async () => {
    if (!inviteLink) return
    setInviteError(null)

    // navigator.clipboard only works on HTTPS or literally "localhost" — fall
    // back to the classic select+execCommand approach for http://dev.* etc.
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(inviteLink)
        setInviteCopied(true)
        return
      } catch (err) {
        // fall through to legacy fallback
      }
    }

    const input = inviteInputRef.current
    if (input) {
      input.focus()
      input.select()
      try {
        if (document.execCommand('copy')) {
          setInviteCopied(true)
          return
        }
      } catch (err) {
        // fall through
      }
    }

    setInviteError('Could not copy link — please copy it manually')
  }

  const inviteEmailValid = isValidEmail(inviteClientEmail)

  const handleSendInvite = () => {
    if (!inviteEmailValid) return
    window.location.href = `mailto:${inviteClientEmail.trim()}?subject=${encodeURIComponent(inviteSubject)}&body=${encodeURIComponent(inviteMessage)}`
  }

  const activeMatterCount = matters.filter((m) => m.status === 'active').length
  const closedMatterCount = matters.filter((m) => m.status === 'closed').length
  const clientsNeedingReviewCount = clients.filter((c) => c.status === 'submitted' || c.status === 'review').length
  const openTasksCount = Object.values(matterTasksByMatterId)
    .flat()
    .filter((t) => t.status !== 'completed').length
  const activeMattersWithTasks = matters.filter((m) => m.status === 'active' && (matterTasksByMatterId[m.id] || []).length > 0)
  const avgTaskCompletionPct = activeMattersWithTasks.length === 0
    ? 0
    : Math.round(
        activeMattersWithTasks.reduce((sum, m) => {
          const t = matterTasksByMatterId[m.id] || []
          return sum + (t.filter((x) => x.status === 'completed').length / t.length) * 100
        }, 0) / activeMattersWithTasks.length
      )
  const mattersByType = matterTypes.map((t) => ({ type: t, count: matters.filter((m) => m.matterType === t).length }))
  const maxMattersByType = Math.max(1, ...mattersByType.map((x) => x.count))
  const clientsByStatus = CLIENT_STATUSES.map((s) => ({ status: s, count: clients.filter((c) => c.status === s).length }))
  const maxClientsByStatus = Math.max(1, ...clientsByStatus.map((x) => x.count))
  // Monotone lightness steps of one hue — this is an ordinal sequence (funnel
  // stage), not distinct series, so it stays a single ramp rather than
  // switching hues per stage.
  const pipelineStepOpacity = (i: number, total: number) => (total <= 1 ? 1 : 0.3 + (i / (total - 1)) * 0.7)
  const upcomingSettlements = matters
    .filter((m) => m.status === 'active' && !!m.settlementDate)
    .sort((a, b) => new Date(a.settlementDate as string).getTime() - new Date(b.settlementDate as string).getTime())
    .slice(0, 5)

  const totalCollectedCents = allPayments.reduce((sum, p) => sum + p.amount, 0)
  const now = new Date()
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
  const paymentsThisMonthCents = allPayments
    .filter((p) => {
      const d = new Date(p.transactionDate || p.createdAt)
      return !Number.isNaN(d.getTime()) && d >= startOfMonth
    })
    .reduce((sum, p) => sum + p.amount, 0)
  const paymentCount = allPayments.length
  const averagePaymentCents = paymentCount === 0 ? 0 : Math.round(totalCollectedCents / paymentCount)
  const recentPayments = allPayments
    .slice()
    .sort((a, b) => new Date(b.transactionDate || b.createdAt).getTime() - new Date(a.transactionDate || a.createdAt).getTime())
    .slice(0, 8)

  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)
  const endOfThisWeek = new Date(startOfToday)
  endOfThisWeek.setDate(endOfThisWeek.getDate() + 7)
  const dueThisWeekCount = matters.filter((m) => {
    if (m.status !== 'active' || !m.settlementDate) return false
    const d = new Date(m.settlementDate)
    return !Number.isNaN(d.getTime()) && d >= startOfToday && d <= endOfThisWeek
  }).length
  const settlementUrgency = (m: Matter): 'overdue' | 'due-soon' | null => {
    if (m.status !== 'active' || !m.settlementDate) return null
    const d = new Date(m.settlementDate)
    if (Number.isNaN(d.getTime())) return null
    if (d < startOfToday) return 'overdue'
    if (d <= endOfThisWeek) return 'due-soon'
    return null
  }
  const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2)}`
  const formatDate = (value?: string | null) => {
    if (!value) return null
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return value
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  }
  const paymentsClientId = route.match(/^\/payments\/(.+)$/)?.[1] || ''
  const matterDetailId = route.match(/^\/matters\/(.+)$/)?.[1] || ''
  const clientDetailId = route.match(/^\/clients\/(.+)$/)?.[1] || ''
  const detailMatter = matterDetailId ? matters.find((m) => m.id === matterDetailId) : undefined
  const detailClient = detailMatter ? clients.find((c) => c.id === detailMatter.primaryClientId) : undefined
  const detailSecondaryClient = detailMatter?.secondaryClientId
    ? clients.find((c) => c.id === detailMatter.secondaryClientId)
    : undefined
  const detailClientRecord = clientDetailId ? clients.find((c) => c.id === clientDetailId) : undefined
  const activeTab = route.startsWith('/payments')
    ? '/payments'
    : route.startsWith('/matters/')
    ? '/matters'
    : route.startsWith('/clients/')
    ? '/clients'
    : (TAB_PATHS as readonly string[]).includes(route) ? route : '/dashboard'
  const filteredClients = clientStatusFilter === 'all' ? clients : clients.filter((c) => c.status === clientStatusFilter)
  const needsMerchantSetup = !!tenantDetails && !tenantDetails.isMerchantConfigured
  const showTermsModal = needsMerchantSetup && !termsAccepted
  const showPlanModal = needsMerchantSetup && termsAccepted && !selectedPlan
  const showMerchantSetupModal = needsMerchantSetup && termsAccepted && !!selectedPlan

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Login />
  }

  return (
    <TerminologyProvider overrides={tenantDetails?.tenant.terminology}>
    <div className="h-screen bg-background flex overflow-hidden">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 shrink-0 min-h-0 flex flex-col transform transition-transform duration-200 ease-in-out md:static md:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ background: 'linear-gradient(180deg, #0F2742 0%, #1F3F66 100%)' }}
      >
        <div className="px-6 py-6 flex items-center gap-2">
          <h1 className="logo logo-on-dark">
            <span className="logo-conveyo">conveyo</span>
            <span className="logo-dot">.</span>
            <span className="logo-one">one</span>
          </h1>
        </div>

        {tenantDetails && (
          <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
            {TAB_PATHS.map((path) => (
              <button
                key={path}
                type="button"
                onClick={() => navigate(path)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === path
                    ? 'bg-white/10 text-white'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 shrink-0">
                  <path strokeLinecap="round" strokeLinejoin="round" d={TAB_ICON_PATHS[path]} />
                </svg>
                {tabLabel(path, terminology)}
              </button>
            ))}
          </nav>
        )}

        {!tenantDetails && <div className="flex-1" />}

        <div className="px-3 py-4 border-t border-white/10 space-y-1">
          <p className="px-3 text-xs text-white/50 truncate mb-2">
            {tenantDetails?.tenant.companyName || tenantDetails?.tenant.email || 'Microservices Demo'}
          </p>

          <button
            type="button"
            onClick={() => setFeedbackOpen(true)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-white/60 hover:text-white hover:bg-white/5 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 shrink-0">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
            </svg>
            Feedback
          </button>

          <button
            type="button"
            onClick={() => navigate('/settings')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              route === '/settings'
                ? 'bg-white/10 text-white'
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 shrink-0">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 0 1 0 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 0 1 0-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281Z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
            </svg>
            Settings
          </button>

          <button
            type="button"
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-white/60 hover:text-white hover:bg-white/5 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 shrink-0">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15M12 9l3 3m0 0-3 3m3-3H2.25" />
            </svg>
            Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 min-h-0 flex flex-col">
        <header className="bg-white shadow-sm shrink-0 md:hidden">
          <div className="px-4 py-3 flex items-center gap-3">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
              className="p-2 -ml-2 text-gray-600 hover:text-gray-900"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
            </button>
            <h1 className="logo">
              <span className="logo-conveyo">conveyo</span>
              <span className="logo-dot">.</span>
              <span className="logo-one">one</span>
            </h1>
          </div>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto">
        <main className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {route === '/settings' && tenantDetails && (
          <SettingsPage tenantDetails={tenantDetails} onMerchantCreated={setTenantDetails} />
        )}

        {route !== '/settings' && tenantDetails && (
          <>
            {activeTab === '/dashboard' && (
              <>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Dashboard</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <Card className="border-l-4 border-primary">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d={TAB_ICON_PATHS['/clients']} />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Total {terminology.clients.toLowerCase()}</p>
                        <p className="text-2xl font-semibold text-primary">{clients.length}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="border-l-4 border-secondary">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-secondary/10 text-secondary flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d={TAB_ICON_PATHS['/matters']} />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Active {terminology.matters.toLowerCase()}</p>
                        <p className="text-2xl font-semibold text-secondary">{activeMatterCount}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="border-l-4 border-amber-500">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Due this week</p>
                        <p className="text-2xl font-semibold text-amber-600">{dueThisWeekCount}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="border-l-4 border-emerald-500">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Closed {terminology.matters.toLowerCase()}</p>
                        <p className="text-2xl font-semibold text-emerald-600">{closedMatterCount}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="border-l-4 border-red-500">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-red-100 text-red-600 flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-8.99 3.75h.008v.008h-.008v-.008Z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">{terminology.clients} needing review</p>
                        <p className="text-2xl font-semibold text-red-600">{clientsNeedingReviewCount}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="border-l-4 border-accent">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-accent/10 text-accent flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d={TAB_ICON_PATHS['/payments']} />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Total collected</p>
                        <p className="text-2xl font-semibold text-accent">{formatCurrency(totalCollectedCents)}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="border-l-4 border-indigo-500">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Open {terminology.tasks.toLowerCase()}</p>
                        <p className="text-2xl font-semibold text-indigo-600">{openTasksCount}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="border-l-4 border-violet-500">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Avg. {terminology.task.toLowerCase()} completion</p>
                        <p className="text-2xl font-semibold text-violet-600">{avgTaskCompletionPct}%</p>
                      </div>
                    </div>
                  </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">{terminology.matters} by Type</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {matters.length === 0 ? (
                        <p className="text-sm text-gray-500">No matters yet</p>
                      ) : (
                        <div className="space-y-3">
                          {mattersByType.map(({ type, count }) => (
                            <div key={type} className="flex items-center gap-3">
                              <span className="w-24 shrink-0 text-sm text-gray-600">{type}</span>
                              <div className="flex-1 h-5 rounded-md bg-gray-100 overflow-hidden">
                                {count > 0 && (
                                  <div
                                    className="h-full bg-accent rounded-r-md"
                                    style={{ width: `${(count / maxMattersByType) * 100}%` }}
                                  />
                                )}
                              </div>
                              <span className="w-5 shrink-0 text-sm font-medium text-gray-900 text-right">{count}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">{terminology.client} Pipeline</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {clients.length === 0 ? (
                        <p className="text-sm text-gray-500">No clients yet</p>
                      ) : (
                        <div className="space-y-3">
                          {clientsByStatus.map(({ status, count }, i) => (
                            <div key={status} className="flex items-center gap-3">
                              <span className="w-24 shrink-0 text-sm text-gray-600 capitalize">{status}</span>
                              <div className="flex-1 h-5 rounded-md bg-gray-100 overflow-hidden">
                                {count > 0 && (
                                  <div
                                    className="h-full rounded-r-md bg-secondary"
                                    style={{
                                      width: `${(count / maxClientsByStatus) * 100}%`,
                                      opacity: pipelineStepOpacity(i, clientsByStatus.length),
                                    }}
                                  />
                                )}
                              </div>
                              <span className="w-5 shrink-0 text-sm font-medium text-gray-900 text-right">{count}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
                  <Card className="lg:col-span-2">
                    <CardHeader>
                      <CardTitle className="text-base">Upcoming Settlements</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {upcomingSettlements.length === 0 ? (
                        <p className="text-sm text-gray-500">No active matters with a settlement date set</p>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {upcomingSettlements.map((m) => {
                            const urgency = settlementUrgency(m)
                            return (
                              <button
                                key={m.id}
                                type="button"
                                onClick={() => navigate(`/matters/${m.id}`)}
                                className="w-full flex items-center justify-between gap-3 py-2.5 text-left hover:bg-gray-50 -mx-2 px-2 rounded-md transition-colors"
                              >
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {clientLabel(m.primaryClientId)}
                                    {m.secondaryClientId ? ` & ${clientLabel(m.secondaryClientId)}` : ''}
                                  </p>
                                  <p className="text-xs text-gray-500 truncate">{m.matterType} &middot; {m.propertyAddress}</p>
                                </div>
                                <div className="shrink-0 text-right">
                                  <p className="text-sm text-gray-900 whitespace-nowrap">{m.settlementDate}</p>
                                  {urgency === 'overdue' && (
                                    <p className="text-xs font-medium text-red-600">Overdue</p>
                                  )}
                                  {urgency === 'due-soon' && (
                                    <p className="text-xs font-medium text-amber-600">Due this week</p>
                                  )}
                                </div>
                              </button>
                            )
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Quick Actions</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-1.5">
                        <button
                          type="button"
                          onClick={() => setShowAddClientModal(true)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 shrink-0 text-primary">
                            <path strokeLinecap="round" strokeLinejoin="round" d={TAB_ICON_PATHS['/clients']} />
                          </svg>
                          Add {terminology.client}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowAddMatterModal(true)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 shrink-0 text-secondary">
                            <path strokeLinecap="round" strokeLinejoin="round" d={TAB_ICON_PATHS['/matters']} />
                          </svg>
                          Open {terminology.matter}
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate('/invitations')}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 shrink-0 text-accent">
                            <path strokeLinecap="round" strokeLinejoin="round" d={TAB_ICON_PATHS['/invitations']} />
                          </svg>
                          Invite {terminology.client}
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}

            {activeTab === '/clients' && clientDetailId && (
              detailClientRecord ? (
                <ClientDetail
                  client={detailClientRecord}
                  matters={matters}
                  onBack={() => navigate('/clients')}
                  onStatusChange={(s) => handleClientStatusChange(detailClientRecord.id, s)}
                  onSelectMatter={(matterId) => navigate(`/matters/${matterId}`)}
                />
              ) : (
                <p className="text-gray-600">{clientsLoading ? 'Loading...' : `${terminology.client} not found`}</p>
              )
            )}

            {activeTab === '/clients' && !clientDetailId && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <Card className="border-l-4 border-primary">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d={TAB_ICON_PATHS['/clients']} />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Total {terminology.clients.toLowerCase()}</p>
                        <p className="text-2xl font-semibold text-primary">{clients.length}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="border-l-4 border-amber-500">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-8.99 3.75h.008v.008h-.008v-.008Z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Needing review</p>
                        <p className="text-2xl font-semibold text-amber-600">{clientsNeedingReviewCount}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="border-l-4 border-emerald-500">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Completed</p>
                        <p className="text-2xl font-semibold text-emerald-600">
                          {clients.filter((c) => c.status === 'completed').length}
                        </p>
                      </div>
                    </div>
                  </Card>
                  <Card className="border-l-4 border-accent">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-accent/10 text-accent flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d={TAB_ICON_PATHS['/payments']} />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Total collected</p>
                        <p className="text-2xl font-semibold text-accent">{formatCurrency(totalCollectedCents)}</p>
                      </div>
                    </div>
                  </Card>
                </div>

                <Card className="mb-8">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>{terminology.clients} ({clients.length})</CardTitle>
                      <button
                        type="button"
                        onClick={() => setShowAddClientModal(true)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-accent text-white hover:opacity-90 transition-opacity"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                        </svg>
                        New {terminology.client}
                      </button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {clientsError && (
                      <Alert type="error" className="mb-4">
                        {clientsError}
                      </Alert>
                    )}

                    <div className="flex flex-wrap gap-2 mb-4">
                      <button
                        type="button"
                        onClick={() => setClientStatusFilter('all')}
                        className={`px-3 py-1 text-sm rounded-lg border ${
                          clientStatusFilter === 'all'
                            ? 'bg-primary border-primary text-white'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        All ({clients.length})
                      </button>
                      {CLIENT_STATUSES.map((s) => {
                        const count = clients.filter((c) => c.status === s).length
                        return (
                          <button
                            key={s}
                            type="button"
                            onClick={() => setClientStatusFilter(s)}
                            className={`px-3 py-1 text-sm rounded-lg border capitalize ${
                              clientStatusFilter === s
                                ? 'bg-primary border-primary text-white'
                                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {s} ({count})
                          </button>
                        )
                      })}
                    </div>

                    {clientsLoading ? (
                      <p className="text-gray-600">Loading...</p>
                    ) : filteredClients.length === 0 ? (
                      <p className="text-gray-600">No {terminology.clients.toLowerCase()} found</p>
                    ) : (
                      <div className="overflow-x-auto -mx-6">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                              <th className="px-6 py-3 font-semibold">{terminology.client}</th>
                              <th className="px-3 py-3 font-semibold">Email</th>
                              <th className="px-3 py-3 font-semibold">{terminology.matters}</th>
                              <th className="px-3 py-3 font-semibold">Status</th>
                              <th className="px-6 py-3" aria-hidden="true" />
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {filteredClients.map((c) => {
                              const matterCount = matters.filter(
                                (m) => m.primaryClientId === c.id || m.secondaryClientId === c.id
                              ).length

                              return (
                                <tr
                                  key={c.id}
                                  className="hover:bg-gray-50 align-top cursor-pointer group"
                                  onClick={() => navigate(`/clients/${c.id}`)}
                                >
                                  <td className="px-6 py-3">
                                    <p className="font-medium text-gray-900">{c.fullName || `${c.firstName} ${c.lastName}`}</p>
                                    <p className="text-xs text-gray-500 mt-0.5">Pinch Payer ID: {c.payerId}</p>
                                  </td>
                                  <td className="px-3 py-3 text-gray-600 whitespace-nowrap">{c.emailAddress}</td>
                                  <td className="px-3 py-3 text-gray-600 whitespace-nowrap">{matterCount}</td>
                                  <td className="px-3 py-3 whitespace-nowrap">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${clientStatusClasses(c.status)}`}>
                                      {c.status}
                                    </span>
                                  </td>
                                  <td className="px-6 py-3 text-right">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 inline-block text-gray-300 group-hover:text-gray-500 transition-colors">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                                    </svg>
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}

            <Modal open={showAddClientModal} onClose={() => setShowAddClientModal(false)} title={`Add ${terminology.client}`}>
              <CreateClientForm
                onCreated={(result: CreateClientResult) => {
                  setClients((prev) => [...prev, result.client])
                  if (result.matter) {
                    setMatters((prev) => [...prev, result.matter as Matter])
                  }
                  if (result.matterError) {
                    setClientsError(`${terminology.client} added, but opening the ${terminology.matter.toLowerCase()} failed: ${result.matterError}`)
                  }
                  setShowAddClientModal(false)
                }}
              />
            </Modal>

            {activeTab === '/invitations' && (
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle>Invitations</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">
                    Send this link to a buyer or seller so they can fill out their own details — no double handling.
                  </p>

                  {inviteError && (
                    <Alert type="error" className="mb-3">
                      {inviteError}
                    </Alert>
                  )}

                  <div className="mb-3 max-w-xs">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{terminology.matter} Type (optional)</label>
                    <select
                      value={inviteMatterType}
                      onChange={(e) => setInviteMatterType(e.target.value as MatterType | '')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                    >
                      <option value="">Just collect details</option>
                      {matterTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      If set, the {terminology.client.toLowerCase()} will also be asked for the property address and settlement date, and a {terminology.matter.toLowerCase()} opens automatically once they submit.
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2 items-center">
                    <button
                      type="button"
                      onClick={handleGenerateInvite}
                      disabled={inviteLoading}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-accent text-white hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
                      </svg>
                      {inviteLoading ? 'Generating...' : `Generate ${terminology.client} Link`}
                    </button>

                    {inviteLink && (
                      <>
                        <input
                          ref={inviteInputRef}
                          readOnly
                          value={inviteLink}
                          onFocus={(e) => e.currentTarget.select()}
                          className="flex-1 min-w-[240px] px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white"
                        />
                        <Button type="button" size="sm" variant="secondary" onClick={handleCopyInvite}>
                          {inviteCopied ? 'Copied!' : 'Copy'}
                        </Button>
                      </>
                    )}
                  </div>

                  {inviteLink && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Send to {terminology.client}</h4>
                      <input
                        type="email"
                        placeholder={`${terminology.client} email`}
                        value={inviteClientEmail}
                        onChange={(e) => setInviteClientEmail(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent mb-2"
                      />
                      <Input
                        label="Email subject"
                        value={inviteSubject}
                        onChange={(e) => setInviteSubject(e.target.value)}
                        className="mb-2"
                      />
                      <div className="mb-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                        <textarea
                          value={inviteMessage}
                          onChange={(e) => setInviteMessage(e.target.value)}
                          rows={6}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                        />
                      </div>
                      <Button
                        type="button"
                        onClick={handleSendInvite}
                        disabled={!inviteEmailValid}
                      >
                        Send Email
                      </Button>
                      <p className="text-xs text-gray-500 mt-2">
                        Enter a valid email above to enable sending. This opens your default email app with the message pre-filled.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {activeTab === '/matters' && matterDetailId && (
              detailMatter ? (
                <MatterDetail
                  matter={detailMatter}
                  client={detailClient}
                  secondaryClient={detailSecondaryClient}
                  clientLabel={clientLabel(detailMatter.primaryClientId)}
                  onBack={() => navigate('/matters')}
                  onMatterUpdated={(updated) => setMatters((prev) => prev.map((mm) => (mm.id === updated.id ? updated : mm)))}
                />
              ) : (
                <p className="text-gray-600">{mattersLoading ? 'Loading...' : `${terminology.matter} not found`}</p>
              )
            )}

            {activeTab === '/matters' && !matterDetailId && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <Card className="border-l-4 border-primary">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d={TAB_ICON_PATHS['/matters']} />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Active {terminology.matters.toLowerCase()}</p>
                        <p className="text-2xl font-semibold text-primary">{activeMatterCount}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="border-l-4 border-amber-500">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Due this week</p>
                        <p className="text-2xl font-semibold text-amber-600">{dueThisWeekCount}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="border-l-4 border-emerald-500">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Closed {terminology.matters.toLowerCase()}</p>
                        <p className="text-2xl font-semibold text-emerald-600">{closedMatterCount}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="border-l-4 border-accent">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-accent/10 text-accent flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d={TAB_ICON_PATHS['/payments']} />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Total collected</p>
                        <p className="text-2xl font-semibold text-accent">{formatCurrency(totalCollectedCents)}</p>
                      </div>
                    </div>
                  </Card>
                </div>

                <Card className="mb-8">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>{terminology.matters} ({matters.length})</CardTitle>
                      <button
                        type="button"
                        onClick={() => setShowAddMatterModal(true)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-accent text-white hover:opacity-90 transition-opacity"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                        </svg>
                        New {terminology.matter}
                      </button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {mattersError && (
                      <Alert type="error" className="mb-4">
                        {mattersError}
                      </Alert>
                    )}

                    {mattersLoading ? (
                      <p className="text-gray-600">Loading...</p>
                    ) : matters.length === 0 ? (
                      <p className="text-gray-600">No {terminology.matters.toLowerCase()} found</p>
                    ) : (
                      <div className="overflow-x-auto -mx-6">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                              <th className="px-6 py-3 font-semibold">{terminology.client}</th>
                              <th className="px-3 py-3 font-semibold">Type</th>
                              <th className="px-3 py-3 font-semibold">Settlement</th>
                              <th className="px-3 py-3 font-semibold">{terminology.tasks}</th>
                              <th className="px-3 py-3 font-semibold">Status</th>
                              <th className="px-6 py-3" aria-hidden="true" />
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {matters.map((m) => {
                              const tasksForMatter = matterTasksByMatterId[m.id] || []
                              const completedCount = tasksForMatter.filter((t) => t.status === 'completed').length
                              const totalCount = tasksForMatter.length
                              const progressPct = totalCount > 0 ? (completedCount / totalCount) * 100 : 0
                              const urgency = settlementUrgency(m)
                              const display = matterDisplayStatus(m, completedCount, totalCount)

                              return (
                                <tr
                                  key={m.id}
                                  className="hover:bg-gray-50 align-top cursor-pointer group"
                                  onClick={() => navigate(`/matters/${m.id}`)}
                                >
                                  <td className="px-6 py-3">
                                    <p className="font-medium text-gray-900">
                                      {clientLabel(m.primaryClientId)}
                                      {m.secondaryClientId ? ` & ${clientLabel(m.secondaryClientId)}` : ''}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-0.5">{m.propertyAddress}</p>
                                  </td>
                                  <td className="px-3 py-3 whitespace-nowrap">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-secondary/10 text-secondary">
                                      {m.matterType}
                                    </span>
                                  </td>
                                  <td className="px-3 py-3 whitespace-nowrap">
                                    <span className="text-gray-900">
                                      {m.status === 'closed' ? 'Settled' : m.settlementDate || 'Not set'}
                                    </span>
                                    {urgency === 'overdue' && (
                                      <span className="block text-xs font-medium text-red-600 mt-0.5">Overdue</span>
                                    )}
                                    {urgency === 'due-soon' && (
                                      <span className="block text-xs font-medium text-amber-600 mt-0.5">Due this week</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-3">
                                    <div className="flex items-center gap-2">
                                      <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                        <div
                                          className="h-full bg-accent rounded-full"
                                          style={{ width: `${progressPct}%` }}
                                        />
                                      </div>
                                      <span className="text-xs text-gray-600 whitespace-nowrap">
                                        {completedCount}/{totalCount}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-3 py-3 whitespace-nowrap">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${display.classes}`}>
                                      {display.label}
                                    </span>
                                  </td>
                                  <td className="px-6 py-3 text-right">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 inline-block text-gray-300 group-hover:text-gray-500 transition-colors">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                                    </svg>
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}

            <Modal open={showAddMatterModal} onClose={() => setShowAddMatterModal(false)} title={`Open ${terminology.matter}`}>
              <CreateMatterForm
                clients={clients}
                onCreated={({ matter }) => {
                  setMatters((prev) => [...prev, matter])
                  setShowAddMatterModal(false)
                }}
              />
            </Modal>

            {activeTab === '/payments' && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <Card className="border-l-4 border-accent">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-accent/10 text-accent flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d={TAB_ICON_PATHS['/payments']} />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Total collected</p>
                        <p className="text-2xl font-semibold text-accent">{formatCurrency(totalCollectedCents)}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="border-l-4 border-emerald-500">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">This month</p>
                        <p className="text-2xl font-semibold text-emerald-600">{formatCurrency(paymentsThisMonthCents)}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="border-l-4 border-primary">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Payments</p>
                        <p className="text-2xl font-semibold text-primary">{paymentCount}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="border-l-4 border-secondary">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-secondary/10 text-secondary flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Average payment</p>
                        <p className="text-2xl font-semibold text-secondary">{formatCurrency(averagePaymentCents)}</p>
                      </div>
                    </div>
                  </Card>
                </div>

                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="text-base">Recent Payments</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {recentPayments.length === 0 ? (
                      <p className="text-sm text-gray-500">No payments yet</p>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {recentPayments.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => navigate(`/payments/${p.clientId}`)}
                            className="w-full flex items-center justify-between gap-3 py-2.5 text-left hover:bg-gray-50 -mx-2 px-2 rounded-md transition-colors"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{clientLabel(p.clientId)}</p>
                              <p className="text-xs text-gray-500 truncate">
                                {p.description || 'Payment'} &middot; {formatDate(p.transactionDate) || formatDate(p.createdAt)}
                              </p>
                            </div>
                            <p className="shrink-0 text-sm font-semibold text-gray-900">{formatCurrency(p.amount)}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="mb-8">
                  <CardHeader>
                    <CardTitle className="text-base">Take a Payment</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-6 max-w-sm">
                      <label className="block text-sm font-medium text-gray-700 mb-1">{terminology.client}</label>
                      <select
                        value={paymentsClientId}
                        onChange={(e) => navigate(e.target.value ? `/payments/${e.target.value}` : '/payments')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                      >
                        <option value="">Select a {terminology.client.toLowerCase()}</option>
                        {clients.map((c) => (
                          <option key={c.id} value={c.id}>{c.fullName || `${c.firstName} ${c.lastName}`}</option>
                        ))}
                      </select>
                    </div>

                    {paymentsClientId ? (
                      <ClientPayments key={paymentsClientId} clientId={paymentsClientId} hideToggle />
                    ) : (
                      <p className="text-gray-600">Select a client to view and take payments.</p>
                    )}
                  </CardContent>
                </Card>
              </>
            )}

            {activeTab === '/templates' && <TemplateLibrary />}
          </>
        )}
      </main>
      </div>
      </div>

      {tenantDetails && <AiAssistant />}
      {tenantDetails && <FeedbackWidget open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />}

      <Modal
        open={showTermsModal}
        onClose={() => {}}
        title="Welcome to Conveyo.One"
        dismissible={false}
        headerAction={<button type="button" onClick={logout} className="text-xs font-medium text-gray-400 hover:text-gray-600">Log out</button>}
      >
        <AcceptTermsForm onAccept={handleAcceptTerms} />
      </Modal>

      <Modal
        open={showPlanModal}
        onClose={() => {}}
        title="Choose Your Plan"
        dismissible={false}
        size="lg"
        headerAction={<button type="button" onClick={logout} className="text-xs font-medium text-gray-400 hover:text-gray-600">Log out</button>}
      >
        <PlanSelector onSelect={handleSelectPlan} />
      </Modal>

      <Modal
        open={showMerchantSetupModal}
        onClose={() => {}}
        title="Set Up Conveyo.One Account"
        dismissible={false}
        headerAction={<button type="button" onClick={logout} className="text-xs font-medium text-gray-400 hover:text-gray-600">Log out</button>}
      >
        <p className="text-sm text-gray-600 mb-4">
          Sign Up — set up your Conveyo.One account to get started.
        </p>
        <CreateMerchantForm onCreated={setTenantDetails} planKey={selectedPlan || undefined} />
      </Modal>
    </div>
    </TerminologyProvider>
  )
}

export default App